import { formatDistanceToNow } from 'date-fns';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { CornerDownRight, Star } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { ReviewPlatformLogo } from '@/components/organisms/settings-review-platforms/ReviewPlatformLogo';
import { cn } from '@/lib/utils';
import type { ReviewPlatform } from '@/types/review-platform';
import { isUnratedReview, UNRATED_LABEL } from './unrated-review';
import { useAutoPostPending, useCancelAutoPost } from '@/hooks/reviews/useAutoPostPending';
import { ReviewDetailDrawer } from './ReviewDetailDrawer';
import { useHasPermission } from '@/hooks/usePermissions';
import type { UnifiedReviewItem } from '@/types/review';
import {
  AUTO_POST_REFUSAL_CAN_STILL_PUBLISH,
  AUTO_POST_REFUSAL_COPY,
  type AutoPostPendingItem,
} from '@/types/auto-post';

/**
 * What auto-post would publish, before it publishes it.
 *
 * Built from the idioms this app already uses for a review rather than a
 * third set: `ReviewPlatformLogo`'s brand tile as the anchor, and the same
 * five-star row `ReviewListRow` draws. Someone arriving from the reviews list
 * should recognise a review here without being told.
 *
 * The reply sits in its own panel with an accent rail, because it is the one
 * thing on the page going onto a public listing under the business's name.
 * An earlier pass rendered it as bare text on a white card and it stopped
 * reading as the subject of the screen at all.
 *
 * Status is a coloured dot plus near-neutral text, per `integration-tokens.ts`
 * — several stacked filled badges read as a row of buttons.
 *
 * The verdict and reason both come from the server, computed by the same
 * `decideAutoPost` the sweep runs. Nothing here re-derives eligibility.
 */
export function AutoPostPreview({
  locationId = null,
}: {
  /**
   * Scopes the preview to one settings row. The endpoint filters by it, so
   * passing it is what stops a location-scoped operator seeing — and being
   * able to cancel — drafts belonging to locations this screen never claimed
   * to cover.
   */
  locationId?: string | null;
}) {
  const { data, isLoading, isError } = useAutoPostPending(locationId);
  const cancel = useCancelAutoPost(locationId);
  const canReply = useHasPermission('reviews:edit');
  // Clicking a row opens the drawer the reviews list already uses, so acting
  // on a review means the same thing here as it does everywhere else.
  const [openReviewId, setOpenReviewId] = useState<string | null>(null);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading drafts…</p>;
  }
  if (isError) {
    return <p className="text-sm text-muted-foreground">Could not load pending auto-posts.</p>;
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed bg-background px-6 py-12 text-center">
        <p className="text-sm font-semibold">No AI drafts waiting</p>
        <p className="mx-auto mt-1.5 max-w-md text-[13px] leading-5 text-muted-foreground">
          Drafts appear here as soon as they are generated, whether or not auto-post is switched on
          — so you can read what would go out before deciding to switch it on.
        </p>
      </div>
    );
  }

  // Grouped rather than mixed with a status word on each row. The question is
  // "what is about to be published in our name", and a flat list made that
  // countable only by reading every row's right-hand corner.
  const going = items.filter(isOnTrack);
  const held = items.filter((item) => !isOnTrack(item));
  const open = items.find((item) => item.externalReviewId === openReviewId) ?? null;

  return (
    <div className="space-y-6">
      {data?.truncated ? (
        // Silence here would be the screen quietly meaning something narrower
        // than it claims: the rows it is not showing are still eligible for
        // the sweep, so a reply could go public having never appeared here.
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-50 px-4 py-2.5 text-[13px] leading-5 text-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
          <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden />
          <p className="min-w-0 flex-1">
            <span className="font-medium">Showing the first {data.shown} drafts.</span> There are
            more waiting, and they can still be published — they are just not on this page yet.
          </p>
        </div>
      ) : null}
      {going.length > 0 ? (
        <Section
          title="Going out"
          count={going.length}
          tone="go"
          caption="Published automatically unless you stop them."
        >
          {going.map((item) => (
            <AutoPostRow
              key={item.externalReviewId}
              item={item}
              onOpen={() => setOpenReviewId(item.externalReviewId)}
              onCancel={() => cancel.mutate({ externalReviewId: item.externalReviewId })}
              cancelling={cancel.isPending}
              canCancel={canReply}
            />
          ))}
        </Section>
      ) : null}

      {held.length > 0 ? (
        <Section
          title="Held"
          count={held.length}
          tone="hold"
          caption="Nothing here goes out on its own. The reason is on each one."
        >
          {held.map((item) => (
            <AutoPostRow
              key={item.externalReviewId}
              item={item}
              onOpen={() => setOpenReviewId(item.externalReviewId)}
              onCancel={() => cancel.mutate({ externalReviewId: item.externalReviewId })}
              cancelling={cancel.isPending}
              canCancel={canReply}
            />
          ))}
        </Section>
      ) : null}

      <ReviewDetailDrawer
        item={open ? toUnifiedReview(open) : null}
        open={openReviewId !== null}
        onOpenChange={(next) => {
          if (!next) setOpenReviewId(null);
        }}
        canReply={canReply}
        canEdit={canReply}
      />
    </div>
  );
}

/**
 * The drawer keys off a composite id and fetches the rest itself, so this
 * carries only what the list already knows rather than duplicating a review.
 */
function toUnifiedReview(item: AutoPostPendingItem): UnifiedReviewItem {
  return {
    id: `ext_${item.externalReviewId}`,
    source: 'external' as UnifiedReviewItem['source'],
    rating: item.rating,
    postedAt: item.reviewPostedAt,
    platform: item.platform as UnifiedReviewItem['platform'],
    author: item.author ?? undefined,
    text: item.reviewText ?? undefined,
  };
}

function Section({
  title,
  count,
  tone,
  caption,
  children,
}: {
  title: string;
  count: number;
  tone: 'go' | 'hold';
  caption: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <span
            className={cn(
              'size-1.5 rounded-full',
              tone === 'go' ? 'bg-emerald-500' : 'bg-amber-500',
            )}
            aria-hidden
          />
          {title}
          <span className="font-normal text-muted-foreground tabular-nums">{count}</span>
        </h3>
        <p className="text-[13px] text-muted-foreground">{caption}</p>
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

/**
 * Going out unless someone intervenes — which includes a draft still inside
 * its delay window. Those are not "held" in any sense the reader cares about:
 * they are the ones there is still time to stop, so they keep the button.
 */
function isOnTrack(item: AutoPostPendingItem) {
  return item.willPost || item.blockedBy === 'delay-not-elapsed';
}

/**
 * Whether to offer the stop button — which is a different question from which
 * group the row is in, and used to be answered with `isOnTrack`.
 *
 * Tying the two together meant a draft held only by a switch could be read but
 * not vetoed. That is the state this screen was built to be read in: the empty
 * copy above invites people to review drafts *before* auto-post is switched
 * on, and every draft is then held by `deployment-disabled`. Under the old
 * rule the only way to reject one was to wait for an administrator to throw
 * the switch and then beat the sweep to it.
 *
 * Which refusals leave a draft still publishable lives with the refusal union
 * in `@/types/auto-post`, so adding a refusal forces the decision.
 */
function isStoppable(item: AutoPostPendingItem) {
  if (item.willPost) return true;
  return item.blockedBy !== null && AUTO_POST_REFUSAL_CAN_STILL_PUBLISH[item.blockedBy];
}

function AutoPostRow({
  item,
  onOpen,
  onCancel,
  cancelling,
  canCancel,
}: {
  item: AutoPostPendingItem;
  onOpen: () => void;
  onCancel: () => void;
  cancelling: boolean;
  /**
   * `reviews:edit`. The tab itself only needs `reviews:view`, so a read-only
   * operator can legitimately be here — and would otherwise be shown a
   * live-looking button to stop a public reply, and told by a 403 that it was
   * never theirs. Reading what is about to be said in your name is a
   * different permission from stopping it.
   */
  canCancel: boolean;
}) {
  const onTrack = isOnTrack(item);
  const stoppable = isStoppable(item);

  return (
    <article
      // The whole row opens the review, because "what else did this customer
      // say" is the question a draft raises and there is nowhere else on this
      // page to answer it. The cancel button stops the bubble so the two
      // actions cannot be confused for one.
      role="button"
      tabIndex={0}
      // Explicit, because the row's text content includes the reply and the
      // cancel button, so its computed name collided with that button's.
      aria-label={`Open review from ${item.author ?? 'this customer'}`}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'w-full cursor-pointer rounded-xl border bg-background p-4 text-left transition-colors',
        'hover:border-foreground/20 hover:bg-muted/30',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
      )}
    >
      <div className="flex items-start gap-3">
        <ReviewPlatformLogo platform={item.platform as ReviewPlatform} className="size-8" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            {item.author ? (
              <span className="truncate text-sm font-semibold">{item.author}</span>
            ) : null}
            <StarRow rating={item.rating} />
            <span className="text-xs text-muted-foreground">{relative(item.reviewPostedAt)}</span>
          </div>
          {item.reviewText ? (
            <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-muted-foreground">
              {item.reviewText}
            </p>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          'mt-3 rounded-lg border-l-2 bg-muted/40 py-2.5 pr-4 pl-3.5',
          onTrack ? 'border-l-emerald-500' : 'border-l-amber-500',
        )}
      >
        <p className="mb-1 flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
          <CornerDownRight className="size-3.5" aria-hidden />
          Our reply
          {item.draftModel === 'template' ? (
            <Badge
              variant="outline"
              className="ml-0.5 h-4 px-1.5 text-[10px] font-normal tracking-normal normal-case"
            >
              template
            </Badge>
          ) : null}
        </p>
        <p className="text-sm leading-6 whitespace-pre-wrap">
          {item.replyText ?? <em className="text-muted-foreground">Empty draft</em>}
        </p>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
        <p
          className="text-[13px] leading-5 text-muted-foreground"
          title={onTrack && item.postAt ? new Date(item.postAt).toLocaleString() : undefined}
        >
          {onTrack ? goesOutCopy(item) : heldCopy(item)}
        </p>
        {stoppable && canCancel ? (
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              onCancel();
            }}
            disabled={cancelling}
            className="ml-auto"
          >
            Don&apos;t post this
          </Button>
        ) : null}
      </div>
    </article>
  );
}

/** The same five-star row `ReviewListRow` draws, so a review reads the same. */
function StarRow({ rating }: { rating: number }) {
  // Routed through the shared predicate rather than testing `rating` directly.
  // A Facebook recommendation arrives with `rating: 0`, and five empty stars
  // announce a score the customer never gave. The guard in
  // `unrated-review.test.ts` exists because that shipped once already.
  if (isUnratedReview({ rating })) {
    return (
      <span className="text-xs text-muted-foreground" aria-label="No star rating">
        {UNRATED_LABEL}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'size-3.5',
            i < rating ? 'fill-yellow-400 text-yellow-400' : 'fill-muted text-muted',
          )}
          aria-hidden
        />
      ))}
    </div>
  );
}

/** Dot plus near-neutral text, per `integration-tokens.ts`. */
function StatusDot({ onTrack }: { onTrack: boolean }) {
  return (
    <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium">
      <span
        className={cn('size-1.5 rounded-full', onTrack ? 'bg-emerald-500' : 'bg-amber-500')}
        aria-hidden
      />
      <span className={onTrack ? 'text-foreground' : 'text-muted-foreground'}>
        {onTrack ? 'Will post' : 'Held'}
      </span>
    </span>
  );
}

/**
 * A window that has already closed must not be described in the past tense.
 *
 * `formatDistanceToNow` with `addSuffix` happily renders "about 1 hour ago"
 * for a `postAt` behind the clock, which read as though the reply had already
 * gone out — while the row still sat there waiting, because the sweep is
 * hourly and had not come round yet. The gap between "eligible" and "posted"
 * is up to an hour wide, and this is the sentence that has to be honest about
 * it: the reply is due, not sent.
 */
function goesOutCopy(item: AutoPostPendingItem) {
  const at = item.postAt ? new Date(item.postAt) : null;
  if (!at || Number.isNaN(at.getTime())) {
    return 'Goes out at the next check.';
  }
  if (at.getTime() <= Date.now()) {
    return 'Due now — goes out at the next check.';
  }
  return `Goes out ${formatDistanceToNow(at, { addSuffix: true })}.`;
}

function heldCopy(item: AutoPostPendingItem) {
  return item.blockedBy ? AUTO_POST_REFUSAL_COPY[item.blockedBy] : 'Held.';
}

function relative(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : formatDistanceToNow(d, { addSuffix: true });
}
